import { McpServerNameSchema } from '@plato/shared';
import { useId, useState } from 'react';
import { ActionError, useAction } from '../../components/action.tsx';
import { useConsole } from '../../data/console-context.tsx';
import type { NewMcpServer } from '../../data/rows.ts';

interface Draft {
  readonly name: string;
  readonly title: string;
  readonly url: string;
  readonly connectWith: NewMcpServer['connectWith'];
  readonly clientId: string;
  readonly clientSecret: string;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  title: '',
  url: '',
  connectWith: 'sign_in',
  clientId: '',
  clientSecret: '',
};

const CONNECT_CHOICES: ReadonlyArray<{ value: Draft['connectWith']; label: string }> = [
  { value: 'sign_in', label: 'Sign in with their account' },
  { value: 'api_key', label: 'Paste their own API key' },
];

export function AddServerForm(props: { onAdded: () => void; onCancel: () => void }) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [problem, setProblem] = useState<string | null>(null);
  const headingId = useId();
  const set = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [field]: value }));

  return (
    <form
      className="form"
      aria-labelledby={headingId}
      onSubmit={(event) => {
        event.preventDefault();
        const found = problemWith(draft);
        setProblem(found);
        if (found !== null) return;
        void action
          .run(async () => {
            await data.createMcpServer(tenant.id, newServer(draft));
          })
          .then((ok) => ok && props.onAdded());
      }}
    >
      <h3 id={headingId}>Add a server</h3>
      <fieldset disabled={action.pending}>
        <label>
          Name
          <span className="hint">
            Its id: lower-case letters, digits and hyphens, up to 32 characters.
          </span>
          <input
            required
            maxLength={32}
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </label>
        <label>
          Title
          <span className="hint">What your users see, such as the service's own name.</span>
          <input
            required
            maxLength={64}
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
          />
        </label>
        <label>
          URL
          <span className="hint">The service's MCP endpoint, starting with https://.</span>
          <input
            type="url"
            required
            value={draft.url}
            onChange={(e) => set('url', e.target.value)}
          />
        </label>
        <fieldset className="choices">
          <legend>How users connect</legend>
          {CONNECT_CHOICES.map((choice) => (
            <label key={choice.value} className="checkbox">
              <input
                type="radio"
                name="connect-with"
                checked={draft.connectWith === choice.value}
                onChange={() => set('connectWith', choice.value)}
              />
              {choice.label}
            </label>
          ))}
        </fieldset>
        {draft.connectWith === 'sign_in' && (
          <details>
            <summary>Use your own OAuth client</summary>
            <p className="hint">
              Only needed when the service accepts only apps registered with it in advance, as
              Square does. Otherwise Plato registers itself.
            </p>
            <label>
              Client ID
              <input
                autoComplete="off"
                value={draft.clientId}
                onChange={(e) => set('clientId', e.target.value)}
              />
            </label>
            <label>
              Client secret
              <input
                type="password"
                autoComplete="off"
                value={draft.clientSecret}
                onChange={(e) => set('clientSecret', e.target.value)}
              />
            </label>
          </details>
        )}
        <p className="hint">
          When your users use this service, their messages and data may be sent to it.
        </p>
        <div className="row">
          <button type="submit" className="primary">
            {action.pending ? 'Adding…' : 'Add server'}
          </button>
          <button type="button" className="quiet" onClick={props.onCancel}>
            Cancel
          </button>
        </div>
      </fieldset>
      {action.pending && draft.connectWith === 'sign_in' && (
        <p role="status" className="muted">
          Checking the server and registering Plato with it. This can take a few seconds.
        </p>
      )}
      <ActionError error={problem ?? action.error} />
    </form>
  );
}

function problemWith(draft: Draft): string | null {
  if (!McpServerNameSchema.safeParse(draft.name).success) {
    return 'The name takes lower-case letters, digits and hyphens, up to 32 characters, and cannot start or end with a hyphen.';
  }
  if (draft.title.trim() === '') return 'Give the server a title.';
  if (!isHttpsUrl(draft.url.trim())) return 'The URL must start with https://.';
  return null;
}

function isHttpsUrl(text: string): boolean {
  try {
    return new URL(text).protocol === 'https:';
  } catch {
    return false;
  }
}

function newServer(draft: Draft): NewMcpServer {
  const signIn = draft.connectWith === 'sign_in';
  const clientId = draft.clientId.trim();
  return {
    name: draft.name,
    title: draft.title.trim(),
    url: draft.url.trim(),
    connectWith: draft.connectWith,
    ...(signIn && clientId !== '' ? { clientId } : {}),
    ...(signIn && draft.clientSecret !== '' ? { clientSecret: draft.clientSecret } : {}),
  };
}
